import { createFileRoute } from '@tanstack/react-router';
import { useParams } from '@tanstack/react-router';
import { useOrganizationSubscription, useOrganizationSubscriptionStatus } from '@/hooks/useOrganizationSubscription';
import { PlansGrid } from '@/components/plans/PlansGrid';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useTRPC } from '@/hooks/useTRPC';
import { useId, useState, useEffect } from 'react';
import { PLANS } from '@envase/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/router';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import {
  CurrentSubscriptionCard,
  CustomerPortalSection,
  CancelSubscriptionButton,
  UpgradePromptCard,
  EnterprisePlanCard,
  PlansPageHeader,
  useCheckoutReturn,
  usePlanSelection,
  UpdateSubscriptionModal,
  AddonsModal,
} from './-components/plans';
import { useOrganizationLimits } from '@/hooks/useOrganizationLimits';
import { useAvailableAddons, useActiveAddons } from '@/hooks/useOrganizationAddons';

export const Route = createFileRoute('/_authenticated/org/$orgId/plans')({
  component: PlansPage,
});

function PlansPage() {
  const { orgId } = useParams({ from: '/_authenticated/org/$orgId/plans' });
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const { data: subscriptionData, isLoading: isLoadingSubscription } = useOrganizationSubscription(orgId);
  const { data: subscriptionStatus, isLoading: isLoadingStatus } = useOrganizationSubscriptionStatus(orgId);
  const { data: limitsData } = useOrganizationLimits(orgId);
  const { data: availableAddons } = useAvailableAddons(orgId);
  const { data: activeAddons } = useActiveAddons(orgId);
  const toast = useToast();
  const { t } = useTranslation();
  const plansGridId = useId();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [updateSubscriptionOpen, setUpdateSubscriptionOpen] = useState(false);
  const [addonsModalOpen, setAddonsModalOpen] = useState(false);

  // Handle return from checkout
  useCheckoutReturn(orgId ?? '', subscriptionData);

  // Get current plan ID
  const currentPlanId = usePlanSelection(subscriptionData);

  const checkoutMutation = useMutation({
    mutationFn: async (planId: string) => {
      if (!orgId) throw new Error('Organization ID is required');
      console.log('[PlansPage] Starting checkout mutation for plan:', planId);
      const result = await trpcClient.organizationSubscription.createCheckout.mutate({
        organizationId: orgId,
        planId,
      });
      console.log('[PlansPage] Checkout mutation result:', result);
      return result;
    },
    onError: (error: Error) => {
      console.error('[PlansPage] Checkout mutation error:', error);
      // Clear session storage on error
      sessionStorage.removeItem('checkoutPlanId');
      sessionStorage.removeItem('checkoutOrgId');
      toast.error(error.message || t('plans.toast.checkoutFailed'));
    },
    onSuccess: (result) => {
      console.log('[PlansPage] Checkout mutation success:', result);
    }
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('Organization ID is required');
      return await trpcClient.organizationSubscription.cancelSubscription.mutate({
        organizationId: orgId,
        cancelAtPeriodEnd: true,
      });
    },
    onSuccess: () => {
      toast.success(t('plans.toast.subscriptionCancelled'));
      // Refetch subscription data
      window.location.reload();
    },
    onError: (error: Error) => {
      toast.error(error.message || t('plans.toast.cancelFailed'));
    },
  });

  const resumeMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error('Organization ID is required');
      return await trpcClient.organizationSubscription.resumeSubscription.mutate({
        organizationId: orgId,
      });
    },
    onSuccess: () => {
      toast.success(t('plans.toast.subscriptionResumed'));
      // Refetch subscription data using tRPC query keys
      queryClient.invalidateQueries({ queryKey: trpc.organizationSubscription.getSubscription.queryKey({ organizationId: orgId }) });
      queryClient.invalidateQueries({ queryKey: trpc.organizationSubscription.isBasicPlan.queryKey({ organizationId: orgId }) });
      queryClient.invalidateQueries({ queryKey: trpc.organizationLimits.getOrganizationLimits.queryKey({ organizationId: orgId }) });
      queryClient.invalidateQueries({ queryKey: trpc.organizationAddons.getActiveAddons.queryKey({ organizationId: orgId }) });
      queryClient.invalidateQueries({ queryKey: trpc.organizationAddons.getAvailableAddons.queryKey({ organizationId: orgId }) });
    },
    onError: (error: Error) => {
      toast.error(error.message || t('plans.toast.resumeFailed'));
    },
  });

  // Purchase Addon mutation
  const purchaseAddonMutation = useMutation({
    mutationFn: async ({ addonType, quantity, stripePriceId }: { 
      addonType: string, 
      quantity: number,
      stripePriceId: string
    }) => {
      if (!orgId || !subscriptionData?.subscription.id) throw new Error('Organization ID or Subscription ID is required');
      return await trpcClient.organizationSubscription.purchaseAddon.mutate({
        organizationId: orgId,
        subscriptionId: subscriptionData.subscription.id,
        addonType: addonType as 'extra_seats' | 'extra_secrets' | 'extra_projects' | 'extra_environments',
        quantity,
        stripePriceId,
      });
    },
    onSuccess: async () => {
      toast.success('Addon purchased successfully!');
      
      // Refetch immediately using tRPC query keys
      queryClient.invalidateQueries({ queryKey: trpc.organizationLimits.getOrganizationLimits.queryKey({ organizationId: orgId }) });
      queryClient.invalidateQueries({ queryKey: trpc.organizationAddons.getActiveAddons.queryKey({ organizationId: orgId }) });
      queryClient.invalidateQueries({ queryKey: trpc.organizationAddons.getAvailableAddons.queryKey({ organizationId: orgId }) });
      queryClient.invalidateQueries({ queryKey: trpc.organizationSubscription.getSubscription.queryKey({ organizationId: orgId }) });
      
      // Poll for updates (webhook might take a moment to sync)
      let attempts = 0;
      const maxAttempts = 10;
      const pollInterval = 1000;
      
      const pollForUpdates = setInterval(async () => {
        attempts++;
        await queryClient.invalidateQueries({ queryKey: trpc.organizationLimits.getOrganizationLimits.queryKey({ organizationId: orgId }) });
        await queryClient.invalidateQueries({ queryKey: trpc.organizationAddons.getActiveAddons.queryKey({ organizationId: orgId }) });
        
        if (attempts >= maxAttempts) {
          clearInterval(pollForUpdates);
        }
      }, pollInterval);
      
      // Clean up after 10 seconds
      setTimeout(() => clearInterval(pollForUpdates), maxAttempts * pollInterval);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to purchase addon');
    },
  });

  // Get Customer Portal URL (only fetch if we have a paid subscription with subscription ID)
  const { data: customerPortal, isLoading: isLoadingPortal } = useQuery({
    queryKey: ['customerPortalUrl', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID is required');
      try {
        const result = await trpcClient.organizationSubscription.getCustomerPortalUrl.query({
          organizationId: orgId,
        });
        console.log('[PlansPage] ✅ Customer Portal URL obtained:', result.portalUrl);
        return result.portalUrl;
      } catch (error) {
        // Log error for debugging but silently fail (free plan or no subscription ID)
        console.warn('[PlansPage] ❌ Failed to get Customer Portal URL:', error);
        console.log('[PlansPage] Debug info:', {
          hasOrgId: !!orgId,
          hasSubscriptionId: !!subscriptionData?.subscription.providerSubscriptionId,
          subscriptionId: subscriptionData?.subscription.providerSubscriptionId,
          error: error instanceof Error ? error.message : String(error),
        });
        return null;
      }
    },
    enabled: !!orgId && !!subscriptionData?.subscription.providerSubscriptionId,
  });

  // Debug: Get detailed sync status
  const { data: debugInfo } = useQuery({
    queryKey: ['subscriptionDebugInfo', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID is required');
      try {
        const result = await trpcClient.organizationSubscription.getSubscriptionDebugInfo.query({
          organizationId: orgId,
        });
        return result;
      } catch (error) {
        console.warn('[PlansPage] Failed to get debug info:', error);
        return null;
      }
    },
    enabled: !!orgId && !!subscriptionData,
  });

  // Check if we're in Stripe test mode (isTestMode is added to debugInfo by the API)
  const isTestMode = (debugInfo as { isTestMode?: boolean })?.isTestMode ?? false;

  // Debug logging
  useEffect(() => {
    if (subscriptionData) {
      const showCustomerPortal = currentPlanId !== 'free' && subscriptionData.subscription.providerSubscriptionId;
      console.log('[PlansPage] 📊 Subscription Debug Info:', {
        status: subscriptionData.subscription.status,
        cancelAtPeriodEnd: subscriptionData.subscription.cancelAtPeriodEnd,
        subscriptionId: subscriptionData.subscription.providerSubscriptionId,
        customerId: subscriptionData.subscription.providerCustomerId,
        planName: subscriptionData.plan.name,
        planSlug: subscriptionData.plan.slug,
        currentPlanId,
        showCustomerPortal,
        customerPortal,
        isLoadingPortal,
        queryEnabled: !!orgId && !!subscriptionData?.subscription.providerSubscriptionId,
        provider: subscriptionData.subscription.provider,
        debugInfo,
      });
    }
  }, [subscriptionData, customerPortal, isLoadingPortal, orgId, debugInfo, currentPlanId]);

  const handlePlanSelect = async (planId: string) => {
    if (planId === currentPlanId) {
      toast.info(t('plans.toast.currentPlan'));
      return;
    }

    const selectedPlan = PLANS.find(p => p.id === planId);
    if (!selectedPlan) {
      toast.error(t('plans.toast.planNotFound'));
      return;
    }

    if (selectedPlan.price === 0) {
      toast.info(t('plans.toast.freePlanSelected'));
      return;
    }

    try {
      // Store that we're going to checkout for this plan
      sessionStorage.setItem('checkoutPlanId', planId);
      sessionStorage.setItem('checkoutOrgId', orgId || '');
      
      // Create checkout session
      console.log('[PlansPage] Creating checkout for plan:', planId);
      const result = await checkoutMutation.mutateAsync(planId);
      
      console.log('[PlansPage] Checkout result:', result);
      
      if (result?.checkoutUrl) {
        // Validate URL before redirecting
        try {
          const url = new URL(result.checkoutUrl);
          
          console.log('[PlansPage] Redirecting to checkout:', url.href);
          
          // Only redirect to valid URLs (https/http)
          if (url.protocol === 'https:' || url.protocol === 'http:') {
            // Use window.location.assign for better redirect handling
            // This allows browser history to work properly
            window.location.assign(result.checkoutUrl);
          } else {
            throw new Error(`Invalid checkout URL protocol: ${url.protocol}`);
          }
        } catch (urlError) {
          console.error('[PlansPage] Invalid checkout URL:', result.checkoutUrl, urlError);
          toast.error(t('plans.toast.invalidCheckoutUrl'));
          // Clear session storage on error
          sessionStorage.removeItem('checkoutPlanId');
          sessionStorage.removeItem('checkoutOrgId');
        }
      } else {
        console.error('[PlansPage] No checkout URL in result:', result);
        toast.error(t('plans.toast.noCheckoutUrl'));
        // Clear session storage on error
        sessionStorage.removeItem('checkoutPlanId');
        sessionStorage.removeItem('checkoutOrgId');
      }
    } catch (error) {
      // Clear session storage on error
      sessionStorage.removeItem('checkoutPlanId');
      sessionStorage.removeItem('checkoutOrgId');
      toast.error(error instanceof Error ? error.message : t('plans.toast.checkoutFailed'));
    }
  };

  return (
    <div className="space-y-6">
      <PlansPageHeader />

      {/* Current Plan Card */}
      {!isLoadingSubscription && subscriptionData && (
        <CurrentSubscriptionCard
          subscriptionData={subscriptionData}
          onUpdateClick={() => setUpdateSubscriptionOpen(true)}
          onResumeClick={() => resumeMutation.mutate()}
          isResuming={resumeMutation.isPending}
          activeAddons={activeAddons}
        >
          {/* Add-ons Button */}
          {limitsData && availableAddons && currentPlanId !== 'free' && subscriptionData.subscription.status === 'active' && (
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAddonsModalOpen(true)}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Manage Add-ons
              </Button>
            </div>
          )}

          {/* Customer Portal Section */}
          {currentPlanId !== 'free' && subscriptionData.subscription.providerSubscriptionId && orgId && (
            <CustomerPortalSection
              organizationId={orgId}
              providerSubscriptionId={subscriptionData.subscription.providerSubscriptionId}
              customerPortal={customerPortal}
              isLoadingPortal={isLoadingPortal}
              isTestMode={isTestMode}
              onOpenPortal={() => customerPortal && window.open(customerPortal, '_blank')}
            />
          )}

          {/* Cancel Subscription Button */}
          {subscriptionData.subscription.status === 'active' && 
           !subscriptionData.subscription.cancelAtPeriodEnd && 
           currentPlanId !== 'free' && (
            <CancelSubscriptionButton
              currentPeriodEnd={new Date(subscriptionData.subscription.currentPeriodEnd)}
              onCancel={() => {
                cancelMutation.mutate(undefined, {
                  onSuccess: () => {
                    setCancelDialogOpen(false);
                  },
                });
              }}
              isCancelling={cancelMutation.isPending}
              modalOpen={cancelDialogOpen}
              onModalOpenChange={setCancelDialogOpen}
            />
          )}
        </CurrentSubscriptionCard>
      )}

      {/* Upgrade Prompt for Basic Plans */}
      {!isLoadingStatus && subscriptionStatus?.shouldShowPrompts && (
        <UpgradePromptCard
          onViewPlans={() => setUpdateSubscriptionOpen(true)}
        />
      )}

      {/* Plans Grid - Only show if no active subscription or on free plan */}
      {(!subscriptionData || currentPlanId === 'free') && (
      <div id={plansGridId}>
        <PlansGrid 
          currentPlanId={currentPlanId}
          onPlanSelect={handlePlanSelect}
          showEnterprise={false}
        />
      </div>
      )}

      {/* Enterprise Plan */}
      <EnterprisePlanCard />

      {/* Modals */}
      {subscriptionData && (
        <>
          <UpdateSubscriptionModal
            open={updateSubscriptionOpen}
            onOpenChange={setUpdateSubscriptionOpen}
            subscriptionData={subscriptionData}
            currentPlanId={currentPlanId}
            onPlanSelect={handlePlanSelect}
          />
          
          {limitsData && availableAddons && currentPlanId !== 'free' && (
            <AddonsModal
              open={addonsModalOpen}
              onOpenChange={setAddonsModalOpen}
          organizationId={orgId!}
          subscriptionId={subscriptionData.subscription.id}
          limits={{
            projects: limitsData.projects,
            environments: limitsData.environments,
            secrets: limitsData.secrets,
            members: limitsData.members,
          }}
          planBaseLimits={{
            projects: subscriptionData.plan.maxProjects,
            environments: subscriptionData.plan.maxEnvironments,
            secrets: subscriptionData.plan.maxSecrets,
            members: subscriptionData.plan.maxMembers,
          }}
          availableAddons={availableAddons}
          onPurchaseAddon={async (addonType: string, quantity: number, stripePriceId: string) => {
                await purchaseAddonMutation.mutateAsync({
              addonType,
              quantity,
              stripePriceId,
            });
          }}
          isLoading={purchaseAddonMutation.isPending}
        />
          )}
        </>
      )}
    </div>
  );
}

